import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Button, message, Spin, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { useAsync } from 'react-async-hook';
import { formatDate, getFullName } from 'utils';
import { actions } from 'features/family';
import commonService from 'services/common.service';
import familyService from 'services/family.service';
import { ChevronRight, CheckmarkFilled } from '@carbon/icons-react';
import Card from 'components/Card/Card';
import { selectUser } from 'features/auth';
import { FAMILY_BRANCH_MAP } from 'containers/Family/AddApplication2/familyApplicationConstants';

const DASHBOARD_STATES = {
  NEW_USER: 'NEW_USER',
  APPLICATION_SUBMITTED: 'APPLICATION_SUBMITTED',
  APPLICATION_IN_PROGRESS: 'APPLICATION_IN_PROGRESS',
};

const APPLICATION_LOADING = { loading: true };

export default function FamilyApplications({ householdId }) {
  const currentUser = useSelector(selectUser);
  const userFullName = getFullName(currentUser);

  const dispatch = useDispatch();
  const history = useHistory();

  const fetchedApplicationAlready = useRef(false);
  const [application, setApplication] = useState(APPLICATION_LOADING);
  const [loadingButtonType, setLoadingButtonType] = useState(null); // 'sponsor' or 'helper'
  useAsync(async () => {
    if (fetchedApplicationAlready.current === true || typeof householdId !== 'number') {
      return;
    }

    try {
      const _application = await commonService.get(`/families/households/${householdId}/applications`, {
        cache: false,
      });
      setApplication(_application);
    } catch (error) {
      if (error.statusCode !== 404) {
        newrelic.noticeError(error);
        message.error('Error fetching applications');
      }

      setApplication(null);
    } finally {
      fetchedApplicationAlready.current = true;
    }
  }, [householdId]);

  useEffect(() => {
    dispatch(actions.setApplication(null));
  }, [dispatch]);

  const dashboardState = useMemo(() => {
    if (application !== null) {
      if (!!application.submittedDate) {
        return DASHBOARD_STATES.APPLICATION_SUBMITTED;
      }

      if (!!application.createdDate) {
        return DASHBOARD_STATES.APPLICATION_IN_PROGRESS;
      }
    }

    return DASHBOARD_STATES.NEW_USER;
  }, [application]);

  // Redirect to submitted page if application is submitted
  useEffect(() => {
    if (dashboardState === DASHBOARD_STATES.APPLICATION_SUBMITTED && application?.id) {
      history.push(`/family/applications/${application.id}/submitted`);
    }
  }, [dashboardState, application?.id, history]);

  const handleCompletedBySponsorUpdate = async (completedBySponsor, buttonType) => {
    if (!application?.id || loadingButtonType) {
      return;
    }

    try {
      setLoadingButtonType(buttonType);

      const updatedApplication = await familyService.saveApplication({
        ...application,
        completedBySponsor,
      });

      setApplication(updatedApplication);
      history.push(`/family/applications/${application.id}`);
    } catch (error) {
      message.error('Something went wrong updating the application');
      newrelic.noticeError(error);
    } finally {
      setLoadingButtonType(null);
    }
  };

  // Off-ramp non-Army incomplete applications
  const applicantBranchId = application?.applicant?.militaryDetails?.branch?.id;
  if (
    application &&
    application !== APPLICATION_LOADING &&
    !application.submittedDate &&
    typeof applicantBranchId === 'number' &&
    applicantBranchId !== FAMILY_BRANCH_MAP.army.id
  ) {
    return <Redirect to="/family/transition" />;
  }

  if (!householdId || application === APPLICATION_LOADING) {
    return (
      <div className="h-full flex flex-col items-center justify-around">
        <Spin className="table m-auto" spinning />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col items-center justify-around">
      <div className="p-6 md:p-0">
        <div className="text-center max-w-[600px] mx-auto">
          <Typography.Title level={3} className="!font-extrabold mb-6">
            Welcome{dashboardState === DASHBOARD_STATES.NEW_USER ? '' : ' back'}, {userFullName}!
          </Typography.Title>
        </div>

        {dashboardState === DASHBOARD_STATES.NEW_USER ? (
          <div className="text-center max-w-[400px] mx-auto mt-14">
            <Button
              type="primary"
              size="large"
              className="w-full mb-3 h-[48px] rounded-lg font-semibold flex items-center justify-center gap-1"
              onClick={() => {
                history.push('/family/new-application');
              }}
            >
              <span>Apply for MCCYN Fee Assistance</span> <ChevronRight className="mb-[-2px]" />
            </Button>

            <Typography.Text type="secondary" className="block text-center">
              Takes about 15 minutes • Save progress anytime
            </Typography.Text>
          </div>
        ) : dashboardState === DASHBOARD_STATES.APPLICATION_SUBMITTED ? (
          <div className="max-w-[600px] mx-auto">
            <Card color="primary" className="py-4 px-8 mt-8">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-200 bg-opacity-50 rounded-full flex items-center justify-center">
                  <CheckmarkFilled size={32} className="text-green-600" />
                </div>
              </div>

              <Typography.Title level={4} className="!font-bold mb-4">
                Application submitted successfully!
              </Typography.Title>

              <Typography.Title level={5} className="!font-medium mb-6 text-gray-700">
                MCCYN Fee Assistance Application
              </Typography.Title>

              <div className="mb-6 space-y-2">
                {application?.householdLegacyId && (
                  <Typography.Text className="block text-gray-600 border-solid border-t border-gray-300 pt-4 mt-2">
                    <strong className="!font-semibold">Family ID:</strong> {application?.householdLegacyId}
                  </Typography.Text>
                )}

                <Typography.Text className="block text-gray-600">
                  <strong className="!font-semibold">Submitted on:</strong>{' '}
                  {application?.submittedDate ? formatDate(application.submittedDate) : 'N/A'}
                </Typography.Text>
              </div>

              <Button
                type="primary"
                size="large"
                className="h-[48px] min-w-full rounded-lg font-semibold flex items-center gap-1 mx-auto mt-16 mb-2"
                onClick={() => {
                  history.push(`/family/applications/${application.id}`);
                }}
              >
                <span>View Application</span>

                <span className="flex-grow" />

                <ChevronRight className="mb-[-2px]" />
              </Button>
            </Card>
          </div>
        ) : (
          <div className="max-w-[600px] mx-auto">
            <Card color="primary" className="py-4 px-8 mt-8">
              <Typography.Title level={5} className="!text-xl !font-bold mb-6 mt-[-4px]">
                MCCYN Fee Assistance Application
              </Typography.Title>

              <div className="mb-6 space-y-2 border-solid border-t border-gray-300 pt-4 mt-2">
                <Typography.Text className="block text-gray-600">
                  <strong className="!font-semibold">Started on:</strong>{' '}
                  {application?.createdDate ? formatDate(application.createdDate) : 'N/A'}
                </Typography.Text>
              </div>

              <div className="mt-12">
                <Button
                  type="primary"
                  size="large"
                  loading={loadingButtonType === 'sponsor'}
                  className="h-[48px] min-w-full rounded-lg font-semibold flex items-center gap-1 mx-auto"
                  onClick={() => {
                    handleCompletedBySponsorUpdate(true, 'sponsor');
                  }}
                >
                  <span>I'm {application?.applicant?.firstName}</span>

                  <span className="flex-grow" />

                  <ChevronRight className="mb-[-2px]" />
                </Button>

                <Button
                  size="large"
                  loading={loadingButtonType === 'helper'}
                  className="mt-4 h-[48px] min-w-full rounded-lg font-semibold flex items-center gap-1 mx-auto"
                  onClick={() => {
                    handleCompletedBySponsorUpdate(false, 'helper');
                  }}
                >
                  <span>I'm helping {application?.applicant?.firstName}</span>

                  <span className="flex-grow" />

                  <ChevronRight className="mb-[-2px]" />
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
